import { useLayoutEffect, useState } from 'react';

type ScaledFormCanvasProps = {
	children: React.ReactNode;
	design?: { w: number; h: number }; // resolución de la maqueta
	activateBelow?: { w?: number; h?: number }; // umbral para activar la escala
	maxScale?: number; // nunca escalar por encima de esto (default 1)
	origin?: 'top-left' | 'center'; // dónde anclar el scale
};

export function ScaledFormCanvas({
	children,
	design = { w: 1536, h: 790 },
	activateBelow, // p.ej. { w: 1440 } o { w: 1280, h: 800 }
	maxScale = 1,
	origin = 'top-left',
}: ScaledFormCanvasProps) {
	const [scale, setScale] = useState(1);

	useLayoutEffect(() => {
		const update = () => {
			const vw = window.visualViewport?.width ?? window.innerWidth;
			const vh = window.visualViewport?.height ?? window.innerHeight;

			// ¿Debemos activar la escala en este viewport?
			const shouldActivate = activateBelow
				? (activateBelow.w ? vw < activateBelow.w : false) ||
				  (activateBelow.h ? vh < activateBelow.h : false)
				: vw < design.w || vh < design.h; // comportamiento actual por defecto

			if (!shouldActivate || vw <= 1000) {
				setScale(1);
				return;
			}

			// Calcula escala para que "quepa" por ancho y alto
			const sx = vw / design.w;
			const sy = vh / design.h;
			const s = Math.min(sx, sy, maxScale);
			setScale(s);
		};

		// rAF para evitar thrashing en resize
		let raf = 0;
		const onResize = () => {
			cancelAnimationFrame(raf);
			raf = requestAnimationFrame(update);
		};

		update();
		window.addEventListener('resize', onResize);
		window.visualViewport?.addEventListener('resize', onResize);
		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener('resize', onResize);
			window.visualViewport?.removeEventListener('resize', onResize);
		};
	}, [activateBelow, design.h, design.w, maxScale]);

	const transformOrigin = origin === 'center' ? 'center' : 'top left';

	return (
		<>
			{scale === 1 ? (
				children
			) : (
				<div className="flex items-center justify-center min-h-[100svh] overflow-hidden">
					<div
						style={{
							width: design.w,
							height: design.h,
							transform: `scale(${scale})`,
							transformOrigin,
						}}
					>
						{children}
					</div>
				</div>
			)}
		</>
	);
}
